import { useRef, useState } from "react";
import { Image as ImageIcon, RefreshCw, Trash2, Upload } from "lucide-react";

import { AvatarMeta } from "../lib/api";
import { DEFAULT_AVATAR_META, normalizeAvatarMeta } from "../lib/avatar";
import { resizeImageToDataUrl } from "../lib/image";
import { ContactAvatar } from "./ContactAvatar";
import { InlineAlert } from "./InlineAlert";

type AvatarUploaderProps = {
  name: string;
  value?: string | null;
  meta?: AvatarMeta | null;
  onChange: (value: string | null, meta: AvatarMeta) => void;
  disabled?: boolean;
};

export function AvatarUploader({ name, value: avatarUrl, meta, onChange, disabled }: AvatarUploaderProps) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [error, setError] = useState<string | null>(null);

  const normalizedMeta = normalizeAvatarMeta(meta);
  const hasAvatar = Boolean(avatarUrl);

  const handleFile = async (file: File | null) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setError("Bitte wähle eine Bilddatei aus.");
      return;
    }

    try {
      setError(null);
      const dataUrl = await resizeImageToDataUrl(file, 720);
      if (dataUrl.length > 5_000_000) {
        setError("Avatar ist zu groß (max. ~5 MB). Bitte ein kleineres Bild wählen.");
        return;
      }
      onChange(dataUrl, { ...DEFAULT_AVATAR_META });
    } catch (err: any) {
      setError(err.message || "Avatar konnte nicht verarbeitet werden.");
    } finally {
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const updateMeta = (key: keyof AvatarMeta, newValue: number) => {
    const next: AvatarMeta = { ...normalizedMeta, [key]: newValue };
    onChange(avatarUrl || null, next);
  };

  const clearAvatar = () => {
    onChange(null, { ...DEFAULT_AVATAR_META });
  };

  const resetCrop = () => {
    onChange(avatarUrl || null, { ...DEFAULT_AVATAR_META });
  };

  return (
    <div className="space-y-3">
      <div className="flex items-start gap-3">
        <ContactAvatar
          contact={{ name: name || "Kontakt", avatar_url: avatarUrl || undefined, avatar_meta: normalizedMeta }}
          size={72}
          enablePreview={hasAvatar}
        />
        <div className="flex-1 space-y-2">
          <div className="text-sm font-semibold text-[var(--color-text)]">Avatar</div>
          <div className="text-xs text-[var(--color-muted)] leading-relaxed">
            Runde Darstellung. Empfohlen: quadratisches Bild, wird automatisch verkleinert (max. 720px Kante).
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={() => inputRef.current?.click()}
              disabled={disabled}
            >
              <Upload className="h-4 w-4" /> Bild hochladen
            </button>
            {hasAvatar && (
              <>
                <button type="button" className="btn btn-secondary btn-sm" onClick={resetCrop} disabled={disabled}>
                  <RefreshCw className="h-4 w-4" /> Ausschnitt zentrieren
                </button>
                <button type="button" className="btn btn-destructive btn-sm" onClick={clearAvatar} disabled={disabled}>
                  <Trash2 className="h-4 w-4" /> Entfernen
                </button>
              </>
            )}
          </div>
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => handleFile(e.target.files?.[0] || null)}
            disabled={disabled}
          />
        </div>
      </div>

      {hasAvatar && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <label className="text-xs text-[var(--color-muted)]">Horizontaler Ausschnitt</label>
            <input
              type="range"
              min={0}
              max={100}
              value={normalizedMeta.x}
              onChange={(e) => updateMeta("x", Number(e.target.value))}
              className="w-full"
              disabled={disabled}
            />
          </div>
          <div>
            <label className="text-xs text-[var(--color-muted)]">Vertikaler Ausschnitt</label>
            <input
              type="range"
              min={0}
              max={100}
              value={normalizedMeta.y}
              onChange={(e) => updateMeta("y", Number(e.target.value))}
              className="w-full"
              disabled={disabled}
            />
          </div>
          <div>
            <label className="text-xs text-[var(--color-muted)]">Zoom</label>
            <input
              type="range"
              min={0.5}
              max={3}
              step={0.05}
              value={normalizedMeta.zoom}
              onChange={(e) => updateMeta("zoom", Number(e.target.value))}
              className="w-full"
              disabled={disabled}
            />
          </div>
        </div>
      )}

      {!hasAvatar && (
        <div className="flex items-center gap-2 text-xs text-[var(--color-muted)]">
          <ImageIcon className="h-4 w-4" />
          Kein Avatar hochgeladen.
        </div>
      )}

      {error && (
        <InlineAlert tone="error" message={error} dismissible onClose={() => setError(null)} />
      )}
    </div>
  );
}
